import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { useUserStore } from '../stores/userStore';
import { authService } from '../services/authService';
import { websocketService } from '../services/websocketService';

interface AuthContextType {
  isInitialized: boolean;
  error: Error | null;
  user: User | null;
  checkSession: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isDev = import.meta.env.DEV;
const debugLog = isDev ? console.log : () => {};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isCheckingRef = useRef(false);
  const mountCountRef = useRef(0);

  // Handle WebSocket connection when user changes
  useEffect(() => {
    if (user) {
      console.log('[Auth Context] User authenticated, connecting to WebSocket');
      websocketService.connect().catch(error => {
        console.error('[Auth Context] WebSocket connection failed:', error);
      });
    } else if (isInitialized) {
      console.log('[Auth Context] User not authenticated, disconnecting WebSocket');
      websocketService.disconnect();
    }
  }, [user, isInitialized]);

  const checkSession = useCallback(async () => {
    if (isCheckingRef.current) {
      console.log('[Auth Context] Session check already in progress, skipping');
      return;
    }
    
    try {
      console.log('[Auth Context] Starting session check');
      isCheckingRef.current = true;
      const { user: sessionUser, error: sessionError } = await authService.getSession();
      
      console.log('[Auth Context] Session check result:', { sessionUser, sessionError });
      
      // If we get a response with no error, update the user
      if (!sessionError && sessionUser) {
        console.log('[Auth Context] Setting user:', sessionUser.email);
        setUser(sessionUser);
        setError(null);
        return;
      }

      console.log('[Auth Context] No valid session found, clearing user');
      // For any kind of auth error, just set user to null without error
      setUser(null);
      setError(null);
    } catch (error) {
      console.error('[Auth Context] Session check error:', error);
      setUser(null);
      setError(error instanceof Error ? error : new Error('Unknown error'));
    } finally {
      isCheckingRef.current = false;
      setIsInitialized(true);
      console.log('[Auth Context] Session check complete');
    }
  }, []); // No dependencies needed

  const signOut = useCallback(async () => {
    try {
      console.log('[Auth Context] Starting sign out');
      // Disconnect WebSocket before clearing state
      websocketService.disconnect();
      
      // Always clear local state first
      setUser(null);
      setError(null);

      // Then try to sign out on the backend
      const { error: signOutError } = await authService.signOut();
      if (signOutError) {
        console.warn('[Auth Context] Backend sign out failed, but user is signed out locally:', signOutError);
      }
    } catch (error) {
      console.warn('[Auth Context] Backend sign out failed, but user is signed out locally:', error);
      // Don't set error state since we've already cleared the user
    }
  }, []);

  useEffect(() => {
    // In development, React Strict Mode will mount components twice
    // We'll only do the session check on the first mount
    mountCountRef.current += 1;
    if (isDev && mountCountRef.current > 1) {
      console.log('[Auth Context] Skipping session check on Strict Mode remount');
      return;
    }

    console.log('[Auth Context] Component mounted, checking session');
    let mounted = true;

    const init = async () => {
      if (mounted) {
        await checkSession();
      }
    };

    init();

    return () => {
      mounted = false;
      isCheckingRef.current = false;
    };
  }, [checkSession]);

  const value = {
    isInitialized,
    error,
    user,
    checkSession,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}